import { LeaveUtilsDataFragment } from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

type LeaveUtilsLeave = LeaveUtilsDataFragment
type LeaveUtilsLeaveType = NonNullable<LeaveUtilsLeave['leaveType']>
type LeaveUtilsAllowance = NonNullable<LeaveUtilsLeaveType['allowances']>[number]

class LeaveUtils {
  static leaveFinishOn = (
    leave?:
      | (Pick<LeaveUtilsLeave, 'startOn' | 'finishOn' | 'finishOnNatural' | 'accrues'> & {
          leaveType?:
            | (Pick<LeaveUtilsLeaveType, 'allowEndless'> & {
                allowances: Pick<LeaveUtilsAllowance, 'daysType'>[]
              })
            | null
        })
      | null
  ) => {
    const startOn = moment(leave?.startOn)
    const allowEndlessAndNoFinishOn = leave?.leaveType?.allowEndless && !leave.finishOn
    const isStartOnGreaterThanToday = startOn.isAfter(moment())
    const finishOn = LeaveUtils.leaveAccruesAndUseNaturalDays(leave)
      ? 'finish_on_natural'
      : 'finish_on'

    if (allowEndlessAndNoFinishOn && !isStartOnGreaterThanToday) {
      return moment()
    }

    if (allowEndlessAndNoFinishOn && isStartOnGreaterThanToday) {
      return startOn
    }

    const attr = finishOn == 'finish_on_natural' ? leave?.finishOnNatural : leave?.finishOn
    return moment(attr)
  }

  static leaveStringDuration = (
    leave?:
      | (Pick<
          LeaveUtilsLeave,
          'durationAttributes' | 'halfDay' | 'startTime' | 'hoursAmountInCents'
        > & {
          leaveType?: Pick<LeaveUtilsLeaveType, 'allowEndless'> | undefined | null
        })
      | null
  ) => {
    const total = LeaveUtils.leaveTotalDaysOrHours(leave)

    if (!LeaveUtils.leaveIsHourly(leave) && leave?.halfDay && total === 0.5) {
      const firstHalf = this.isHalfDayBeginningOfDay(leave)

      return firstHalf
        ? i18n.t('time_off.half_day_item.beggining_of_day')
        : i18n.t('time_off.half_day_item.end_of_day')
    }

    return LeaveUtils.leaveIsHourly(leave)
      ? i18n.tp('time_off.hours_with_years', {
          count: total,
        })
      : i18n.tp('time_off.days_with_years', {
          count: total,
        })
  }

  static leaveUnits = (
    leave?:
      | (Pick<LeaveUtilsLeave, 'durationAttributes'> & {
          leaveType?: Pick<LeaveUtilsLeaveType, 'allowEndless' | 'workable'> | undefined | null
        })
      | null
  ) => {
    const type = LeaveUtils.leaveIsHourly(leave) ? 'hours' : 'days'

    const duration = LeaveUtils.durationAttributes(leave)

    const units = leave?.leaveType?.workable ? duration.workable_units : duration.used_units

    // If units is undefined means duration attributes is null, let's return zero value (0).
    if (units === undefined) {
      return 0
    }

    return units[type]
  }

  static durationAttributes = (leave?: Pick<LeaveUtilsLeave, 'durationAttributes'> | null) => {
    if (!leave?.durationAttributes) return ''

    let rubyString = leave.durationAttributes.replace(/=>/g, ':')

    // Step 2: Wrap dynamic date strings in quotes
    rubyString = rubyString.replace(
      /\b[A-Z][a-z]{2}, \d{1,2} [A-Z][a-z]{2} \d{4}\b/g,
      (match) => `"${match}"`
    )

    // Step 3: Wrap numeric keys (like 2024) in quotes
    rubyString = rubyString.replace(/(\{|, )(\d+):/g, '$1"$2":')

    return rubyString ? JSON.parse(rubyString) : ''
  }

  static leaveTotalDaysOrHours = (leave?: Pick<LeaveUtilsLeave, 'durationAttributes'> | null) => {
    const units = LeaveUtils.leaveUnits(leave)

    if (units === undefined) {
      return 0
    }

    return Object.keys(units)
      .map((year) => units[year])
      .reduce<number>((a, b) => {
        if (b !== undefined) {
          return a + b
        }

        return a
      }, 0)
  }

  static leaveAccruesAndUseNaturalDays = (
    leave?:
      | (Pick<LeaveUtilsLeave, 'accrues'> & {
          leaveType?:
            | (Pick<LeaveUtilsLeaveType, 'allowEndless'> & {
                allowances: Pick<LeaveUtilsAllowance, 'daysType'>[]
              })
            | null
        })
      | null
  ) => {
    const allowance = leave?.leaveType && leave.leaveType.allowances?.[0]
    const naturalDays = Boolean(
      allowance &&
      (LeaveUtils.natural(allowance) ||
        LeaveUtils.frenchOuvre(allowance) ||
        LeaveUtils.frenchCalendarDays(allowance))
    )

    return leave?.accrues && naturalDays
  }

  static isHalfDayBeginningOfDay(leave: Pick<LeaveUtilsLeave, 'halfDay'>): boolean {
    return leave.halfDay === 'beggining_of_day'
  }

  static natural(allowance: Pick<LeaveUtilsAllowance, 'daysType'>): boolean {
    return allowance.daysType === 'natural_days'
  }

  static frenchCalendarDays(allowance: Pick<LeaveUtilsAllowance, 'daysType'>): boolean {
    return allowance.daysType === 'french_calendar_days'
  }

  static frenchOuvre(allowance: Pick<LeaveUtilsAllowance, 'daysType'>): boolean {
    return allowance.daysType === 'french_ouvres'
  }

  static leaveIsHourly = (
    leave?: {
      startTime?: LeaveUtilsLeave['startTime']
      hoursAmountInCents?: LeaveUtilsLeave['hoursAmountInCents']
      leaveType?: Pick<LeaveUtilsLeaveType, 'allowEndless'> | undefined | null
    } | null,
    allowances?: Pick<LeaveUtilsAllowance, 'allowanceType'>[]
  ) => {
    const isHourlyForEmployee = !!LeaveUtils.isHourlyForEmployee(allowances)

    // In very rare cases the allowance is not being properly loaded in the isHourlyForEmployee(employee)
    // function. With this quick fix we still want to return true for hourly leaves.
    const isHourlyForLeave = !!(leave?.startTime && leave?.hoursAmountInCents)

    return isHourlyForEmployee || isHourlyForLeave
  }

  static isHourlyForEmployee(
    allowancesResouces?: Pick<LeaveUtilsAllowance, 'allowanceType'>[]
  ): boolean {
    return allowancesResouces?.[0]?.allowanceType === 'hours'
  }
}

export default LeaveUtils
